import type {AxiosError} from "axios";
import type {SeverityLevel} from "@sentry/react";

import {sessionCaptureOnce} from "./capture-session";
import {
  SHIFT_DOMAIN_ERROR_CODES,
  SHIFT_BREAK_GLITCHTIP_CAPTURE_ALLOWLIST_4XX,
} from "./error-codes";
import {DEVICE_FAILURE_ERROR_CODES, type FailureDomain} from "./failure-domains";
import {extractHttpContextFromAxios, type HttpContext, normalizeHttpPathTag} from "./http-context";
import type {GlitchtipDiagnostics} from "./diagnostics";

export type GlitchtipCaptureGateInput = {
  error?: unknown;
  feature?: string;
  errorCode?: string;
  eventType?: string;
  sheetId?: string;
  failureDomain?: FailureDomain;
  operation?: string;
  errorSource: "axios" | "boundary" | "domain" | "telemetry";
  axiosError?: AxiosError;
  http?: HttpContext;
  level?: SeverityLevel;
  extra?: Record<string, string | number | undefined>;
  diagnostics?: GlitchtipDiagnostics;
};

export const TELEMETRY_EVENT_TYPES = new Set([
  "photo_attached",
  "camera_flash_probed",
  "camera_flash_toggled",
  "gps_fallback_used",
  "client_validation_failure",
  "glitchtip_client_boot",
]);

/** Background polls — max one GlitchTip event per session per path. */
export const BACKGROUND_POLL_HTTP_PATHS = new Set([
  "notifications",
  "briefingboard/pending",
]);

const EXPECTED_BUSINESS_ERROR_CODES = new Set<string>([
  SHIFT_DOMAIN_ERROR_CODES.BREAK_BEFORE_SCHEDULED_TIME,
  SHIFT_DOMAIN_ERROR_CODES.BREAK_EXTENDS_PAST_SHIFT_END,
  SHIFT_DOMAIN_ERROR_CODES.NO_ACTIVE_BREAK,
  DEVICE_FAILURE_ERROR_CODES.FILE_PICK_CANCELLED,
]);

const GATED_PERMISSION_ERROR_CODES = new Set<string>([
  DEVICE_FAILURE_ERROR_CODES.CAMERA_PERMISSION_DENIED,
  DEVICE_FAILURE_ERROR_CODES.GPS_PERMISSION_DENIED,
  DEVICE_FAILURE_ERROR_CODES.PUSH_PERMISSION_DENIED,
]);

function resolveAxiosError(input: GlitchtipCaptureGateInput): AxiosError | undefined {
  if (input.axiosError) return input.axiosError;
  if (input.error && typeof input.error === "object" && "isAxiosError" in input.error) {
    return input.error as AxiosError;
  }
  const cause =
    input.error && typeof input.error === "object" && "cause" in input.error
      ? (input.error as {cause?: unknown}).cause
      : undefined;
  if (cause && typeof cause === "object" && "isAxiosError" in cause) {
    return cause as AxiosError;
  }
  return undefined;
}

function isExpectedBusiness4xx(input: GlitchtipCaptureGateInput): boolean {
  if (input.errorCode && EXPECTED_BUSINESS_ERROR_CODES.has(input.errorCode)) {
    return true;
  }

  const axiosError = resolveAxiosError(input);
  if (!axiosError?.response) return false;

  const status = axiosError.response.status;
  if (status < 400 || status >= 500) return false;

  const bodyCode = (axiosError.response.data as {error_code?: string} | undefined)?.error_code;
  if (typeof bodyCode === "string" && EXPECTED_BUSINESS_ERROR_CODES.has(bodyCode)) {
    return true;
  }

  if (
    typeof bodyCode === "string" &&
    SHIFT_BREAK_GLITCHTIP_CAPTURE_ALLOWLIST_4XX.has(bodyCode)
  ) {
    return true;
  }

  return false;
}

function isUserPermissionDismissal(input: GlitchtipCaptureGateInput): boolean {
  if (input.errorCode && GATED_PERMISSION_ERROR_CODES.has(input.errorCode)) {
    return false;
  }

  const error = input.error;
  if (!error) return false;

  const name = error instanceof Error ? error.name : "";
  const message = (
    error instanceof Error ? error.message : typeof error === "string" ? error : ""
  ).toLowerCase();

  if (name === "NotAllowedError") return true;
  if (message.includes("permission denied") || message.includes("permission dismissed")) {
    return true;
  }
  if (message.includes("notallowederror")) return true;

  return false;
}

function isBackgroundPollPath(httpPath?: string): boolean {
  if (!httpPath) return false;
  if (BACKGROUND_POLL_HTTP_PATHS.has(httpPath)) return true;
  return Array.from(BACKGROUND_POLL_HTTP_PATHS).some(
    (prefix) => httpPath === prefix || httpPath.startsWith(`${prefix}/`),
  );
}

function shouldSampleBackgroundPoll(input: GlitchtipCaptureGateInput): boolean {
  const axiosError = resolveAxiosError(input);
  const httpPath = input.http?.httpPath ?? (axiosError ? extractHttpContextFromAxios(axiosError).httpPath : undefined);
  if (!isBackgroundPollPath(httpPath)) return true;
  return sessionCaptureOnce(`background-poll:${httpPath}`);
}

/** Central gate — returns false when the event must not reach GlitchTip. */
export function shouldCaptureToGlitchtip(input: GlitchtipCaptureGateInput): boolean {
  if (input.eventType && TELEMETRY_EVENT_TYPES.has(input.eventType)) return false;
  if (input.level === "info") return false;
  if (input.errorSource === "telemetry") return false;
  if (isExpectedBusiness4xx(input)) return false;
  if (isUserPermissionDismissal(input)) return false;
  if (!shouldSampleBackgroundPoll(input)) return false;
  return true;
}

/** Belt-and-suspenders drop for events that already reached beforeSend. */
export function shouldDropEventByTags(tags: Record<string, unknown> | undefined): boolean {
  if (!tags) return false;
  if (tags.error_source === "telemetry") return true;
  if (tags.level === "info") return true;

  const eventType = tags.event_type;
  if (typeof eventType === "string" && TELEMETRY_EVENT_TYPES.has(eventType)) {
    return true;
  }

  const errorCode = tags.error_code;
  if (typeof errorCode === "string" && EXPECTED_BUSINESS_ERROR_CODES.has(errorCode)) {
    return true;
  }

  return false;
}

export function isBackgroundPollHttpPath(url?: string): boolean {
  return isBackgroundPollPath(normalizeHttpPathTag(url));
}
